import type { ProjectDetail, ProjectSettingResponse } from "@esi/shared";
import { t as translate } from "../../i18n/i18n.ts";
import { PROJECT_SETTINGS_GROUPS, type ProjectSettingsGroup } from "../../router.tsx";
import type { ProvenanceInfo } from "../../settings-shell/ProvenanceMark.tsx";
import { can } from "../capabilities.ts";
import { BEHAVIOUR, type BehaviourDef } from "./behaviour.ts";

type Block = BehaviourDef["group"];

/** The pages holding behaviour settings, and the blocks (sections) each shows, in order. */
export const BEHAVIOUR_PAGES = {
  assistant: ["answers", "models"],
  knowledge: ["knowledge", "wiki", "mcp"],
  web: ["web"],
} as const satisfies Partial<Record<ProjectSettingsGroup, readonly Block[]>>;

export type BehaviourPage = keyof typeof BEHAVIOUR_PAGES;

export const isBehaviourPage = (group: ProjectSettingsGroup): group is BehaviourPage =>
  group in BEHAVIOUR_PAGES;

const PAGE_OF_BLOCK = new Map<Block, BehaviourPage>(
  Object.entries(BEHAVIOUR_PAGES).flatMap(([page, blocks]) =>
    blocks.map((block) => [block, page as BehaviourPage] as const),
  ),
);

/** The page a setting is on: its block's page; a key the app does not know lands on General. */
export function pageOf(key: string): ProjectSettingsGroup {
  const block = BEHAVIOUR[key]?.group;
  return (block && PAGE_OF_BLOCK.get(block)) ?? "general";
}

/** The groups this caller sees: the danger zone only with `project.delete`. */
export const visibleGroups = (project: ProjectDetail): ProjectSettingsGroup[] =>
  PROJECT_SETTINGS_GROUPS.filter((g) => g !== "danger" || can(project, "project.delete"));

/** The single-page settings' former anchors (`#members`), each opening its group. */
const FORMER_ANCHORS: Readonly<Record<string, ProjectSettingsGroup>> = {
  "#general": "general",
  "#requests": "members",
  "#members": "members",
  "#behaviour": "assistant",
  "#danger": "danger",
};

export const groupOfAnchor = (hash: string): ProjectSettingsGroup | null =>
  FORMER_ANCHORS[hash] ?? null;

/** The owning entity's name when it is the level a value comes from, else null. */
function owningEntity(
  setting: ProjectSettingResponse,
  project: Pick<ProjectDetail, "owner" | "ownerName">,
): string | null {
  return setting.sourceEntityId &&
    project.owner.kind === "entity" &&
    project.owner.entityId.toLowerCase() === setting.sourceEntityId.toLowerCase()
    ? project.ownerName
    : null;
}

/** Set for this project and in effect: a value locked above wins over the project's own one. */
export const isSetForProject = (setting: ProjectSettingResponse) =>
  setting.source === "project" && !setting.locked;

/**
 * Where a project setting's value comes from, as the project settings response states it, with
 * the backoffice's marks: set for this project, inherited from the organisation or an entity
 * (named only when it owns the project), locked by the level it comes from. A plain deployment
 * default has no mark (the row says "Default"); the project has no level below to lock.
 */
export function projectProvenance(
  setting: ProjectSettingResponse,
  project: Pick<ProjectDetail, "owner" | "ownerName">,
): ProvenanceInfo[] {
  const marks: ProvenanceInfo[] = [];
  const entity = owningEntity(setting, project);
  if (isSetForProject(setting)) {
    marks.push({ kind: "overridden", label: translate("settings.behaviour.source.project") });
  } else if (setting.source === "tenant") {
    marks.push({ kind: "inherited", label: translate("settings.behaviour.source.tenant") });
  } else if (setting.source === "entity") {
    marks.push({
      kind: "inherited",
      label: entity
        ? translate("settings.behaviour.source.entity", { entity })
        : translate("settings.behaviour.source.entityUnknown"),
    });
  }
  if (setting.locked) {
    // Locked, the effective value is the locked one: its source is the locking level.
    marks.push({
      kind: "lockedAbove",
      label:
        setting.source === "instance"
          ? translate("settings.behaviour.locked.instance")
          : setting.source === "entity"
            ? entity
              ? translate("settings.behaviour.locked.entity", { entity })
              : translate("settings.behaviour.locked.entityUnknown")
            : translate("settings.behaviour.locked.tenant"),
    });
  }
  return marks;
}
